import { useEffect, useState } from 'react';

import { getAuthHeaders } from '@/identity.js';

/** Which entry point a report came from. The server words the ticket accordingly. */
export type SupportTicketKind = 'bug' | 'feedback';

/**
 * What the browser and the app knew when the report was written. Everything here is evidence for
 * whoever picks the ticket up — the reporter's identity comes from the platform JWT, never from
 * this object.
 */
export interface SupportTicketContext {
  pageUrl?: string;
  browser?: string;
  os?: string;
  screen?: string;
  timeZone?: string;
  appBuild?: string;
  userAgent?: string;
  workspaceId?: string | null;
  conversationId?: string | null;
  messageId?: string | null;
  /** The error line a failed chat answer showed, when the report was sent from it. */
  errorText?: string | null;
}

export interface CreateSupportTicketInput {
  kind: SupportTicketKind;
  description: string;
  context: SupportTicketContext;
}

export interface CreateFeatureRequestInput {
  description: string;
  context: SupportTicketContext;
}

export interface SimilarFeatureRequest {
  issueKey: string;
  summary: string;
  status: string;
  url: string;
}

/** The filed ticket. `duplicate` means an identical report had just created it. */
export interface CreatedSupportTicket {
  issueKey: string;
  url: string;
  raisedOnBehalfOf: boolean;
  duplicate: boolean;
}

const SUPPORT_TICKETS_ENDPOINT = '/v1/support/tickets';
const SUPPORT_CONFIG_ENDPOINT = '/v1/support/config';
const FEATURE_REQUESTS_ENDPOINT = '/v1/support/feature-requests';

/**
 * The REST wire here is snake_case, like every other contract between the SPA and a NestJS service.
 * The server validates with `whitelist: true`, which drops an unknown field silently — so a
 * camelCase key would not fail, it would simply never arrive. Mapping in one place is what keeps
 * that from happening quietly.
 */
function wireContext(context: SupportTicketContext): Record<string, string> {
  const pairs: Array<[string, string | null | undefined]> = [
    ['page_url', context.pageUrl],
    ['browser', context.browser],
    ['os', context.os],
    ['screen', context.screen],
    ['time_zone', context.timeZone],
    ['app_build', context.appBuild],
    ['user_agent', context.userAgent],
    ['workspace_id', context.workspaceId],
    ['conversation_id', context.conversationId],
    ['message_id', context.messageId],
    ['error_text', context.errorText],
  ];
  return Object.fromEntries(
    pairs.filter((pair): pair is [string, string] => Boolean(pair[1])),
  );
}

/**
 * How long filing may take before the caller is told it did not land. Jira behind the gateway is
 * the slow part, and the server gives up on it at 15s, so this sits just past that.
 */
const TICKET_TIMEOUT_MS = 20000;

/**
 * A deadline for one fetch, as an `AbortController` rather than `AbortSignal.timeout`. The static
 * helper is missing in some of the runtimes this app is served in, and reading it there throws
 * before the request is ever made - `loadRuntimeConfig` in `runtime-config.ts` avoids it for the
 * same reason. Returns the signal and the timer the caller must clear.
 */
function deadline(ms: number): { signal: AbortSignal; done: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, done: () => clearTimeout(timer) };
}

/**
 * Files a Jira Service Desk ticket for the signed-in employee.
 *
 * Rejects with the server's own message where there is one — "not configured", "too many tickets",
 * "Jira did not accept the ticket" are all things the person clicking the button should read
 * verbatim rather than as a status code.
 */
export async function createSupportTicket(
  input: CreateSupportTicketInput,
): Promise<CreatedSupportTicket> {
  const headers = await getAuthHeaders();
  /**
   * The deadline covers reading the body, not just getting the headers. A server that answers and
   * then stalls mid-body is the same "Sending…" forever from where the reporter is sitting.
   */
  const { signal, done } = deadline(TICKET_TIMEOUT_MS);
  try {
    const response = await fetch(SUPPORT_TICKETS_ENDPOINT, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        kind: input.kind,
        description: input.description,
        context: wireContext(input.context),
      }),
      signal,
    });

    if (!response.ok) {
      throw new Error(await failureMessage(response));
    }

    const body = (await response.json()) as {
      issue_key: string;
      url: string;
      raised_on_behalf_of: boolean;
      duplicate: boolean;
    };
    return {
      issueKey: body.issue_key,
      url: body.url,
      raisedOnBehalfOf: body.raised_on_behalf_of,
      duplicate: body.duplicate,
    };
  } catch (error) {
    /**
     * The only honest thing to say once the deadline has fired: the request left, and we never
     * learned what became of it.
     */
    if (signal.aborted) {
      throw new Error(
        'The server did not answer in time. Your report may not have been filed.',
      );
    }
    throw error;
  } finally {
    done();
  }
}

export async function createFeatureRequest(
  input: CreateFeatureRequestInput,
): Promise<CreatedSupportTicket> {
  return postCreatedTicket(FEATURE_REQUESTS_ENDPOINT, {
    description: input.description,
    context: wireContext(input.context),
  });
}

export async function findSimilarFeatureRequests(
  description: string,
): Promise<SimilarFeatureRequest[]> {
  const body = await postJson<{
    matches?: Array<{
      issue_key: string;
      summary: string;
      status: string;
      url: string;
    }>;
  }>(`${FEATURE_REQUESTS_ENDPOINT}/similar`, { description });
  return (body.matches ?? []).map((match) => ({
    issueKey: match.issue_key,
    summary: match.summary,
    status: match.status,
    url: match.url,
  }));
}

export async function joinFeatureRequest(issueKey: string): Promise<void> {
  await postJson(
    `${FEATURE_REQUESTS_ENDPOINT}/${encodeURIComponent(issueKey)}/join`,
    undefined,
  );
}

async function postCreatedTicket(
  url: string,
  body: unknown,
): Promise<CreatedSupportTicket> {
  const wire = await postJson<{
    issue_key: string;
    url: string;
    raised_on_behalf_of: boolean;
    duplicate: boolean;
  }>(url, body);
  return {
    issueKey: wire.issue_key,
    url: wire.url,
    raisedOnBehalfOf: wire.raised_on_behalf_of,
    duplicate: wire.duplicate,
  };
}

/**
 * The deadline covers reading the body, not just getting the headers — same reasoning as
 * `createSupportTicket`'s own deadline. A caller that does not need the body (`joinFeatureRequest`)
 * still gets `{}` parsed and discarded; every response here is small JSON, so that costs nothing.
 */
async function postJson<T>(url: string, body: unknown): Promise<T> {
  const headers = await getAuthHeaders();
  const { signal, done } = deadline(TICKET_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal,
    });
    if (!response.ok) throw new Error(await failureMessage(response));
    return (await response.json()) as T;
  } catch (error) {
    if (signal.aborted) {
      throw new Error('The server did not answer in time. Please try again.');
    }
    throw error;
  } finally {
    done();
  }
}

/** The server's explanation if it sent one, else something that at least names the status. */
async function failureMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string | string[] };
    const message = Array.isArray(body.message)
      ? body.message.join(', ')
      : body.message;
    if (message) return message;
  } catch {
    /* A body that is not JSON tells us nothing beyond the status. */
  }
  return `Ticket creation failed (${response.status}).`;
}

/**
 * How long the capability lookup may take before it counts as a no. The answer is cached for the
 * page, and `submitFeedback` awaits it after the feedback POST, so a request a stalled gateway
 * never closes would leave that turn hanging for the rest of the session.
 */
const CONFIG_TIMEOUT_MS = 8000;

/**
 * Whether this environment can file tickets at all — it needs a Jira service-account token, which
 * a local stack or a fresh environment may not have. Fail-closed: anything unexpected hides the
 * affordance, because a button that always errors is worse than no button.
 */
async function fetchSupportEnabled(): Promise<boolean> {
  const { signal, done } = deadline(CONFIG_TIMEOUT_MS);
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(SUPPORT_CONFIG_ENDPOINT, {
      headers,
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) return false;
    const body = (await response.json()) as { enabled?: boolean };
    return body.enabled === true;
  } catch {
    return false;
  } finally {
    done();
  }
}

/**
 * The answer, asked for once per page load.
 *
 * Whether an environment has a Jira service account does not change while somebody is sitting in
 * the app, and both callers mount often — the chat feedback popup is created and destroyed on every
 * vote — so a shared promise is the whole caching story needed here.
 */
let pending: Promise<boolean> | null = null;

export function loadSupportTicketsEnabled(): Promise<boolean> {
  pending ??= fetchSupportEnabled();
  return pending;
}

/** Drops the cached answer. Tests need this; nothing in the app does. */
export function resetSupportTicketsCache(): void {
  pending = null;
}

/** React hook for "can this environment file tickets" — false until proven otherwise. */
export function useSupportTicketsEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    void loadSupportTicketsEnabled().then((answer) => {
      if (live) setEnabled(answer);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
